import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Sparkles, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STEP = {
    queued: "Queued",
    extracting: "Extracting text from PDF",
    "ai-formatting": "AI is formatting the lesson",
    "rendering-pages": "Rendering page images",
    done: "Done",
    failed: "Failed",
};

export default function ConvertPdfDialog({ open, onOpenChange, lessonId, lessonTitle, onDone }) {
    const [mode, setMode] = useState("faithful");
    const [submitting, setSubmitting] = useState(false);
    const [job, setJob] = useState(null);
    const pollRef = useRef(null);

    useEffect(() => {
        if (open) {
            setMode("faithful");
            setSubmitting(false);
            setJob(null);
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [open]);

    useEffect(() => {
        if (!job?.id || job.status === "done" || job.status === "failed") return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/lesson-convert-jobs/${job.id}`);
                setJob(data);
                if (data.status === "done" || data.status === "failed") {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    if (data.status === "done") {
                        toast.success("Lesson converted!");
                        onDone?.();
                    } else {
                        toast.error(data.error || "Conversion failed.");
                    }
                }
            } catch {
                // keep polling — transient errors only
            }
        }, 1500);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [job?.id, job?.status, onDone]);

    const submit = async () => {
        setSubmitting(true);
        try {
            const { data } = await api.post(`/lessons/${lessonId}/convert-pdf`, { mode });
            const initial = await api.get(`/lesson-convert-jobs/${data.job_id}`);
            setJob(initial.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start conversion.");
        } finally {
            setSubmitting(false);
        }
    };

    let statusLine = job ? (STEP[job.status] || job.status) : "";
    if (job?.status === "rendering-pages" && job.render_progress?.total) {
        statusLine = `Rendering page ${job.render_progress.done} / ${job.render_progress.total}`;
    }

    const finished = job && (job.status === "done" || job.status === "failed");

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="convert-pdf-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        Convert PDF to readable lesson
                    </DialogTitle>
                    <DialogDescription>
                        {lessonTitle ? <>Lesson: <strong>{lessonTitle}</strong></> : null}
                    </DialogDescription>
                </DialogHeader>

                {!job ? (
                    <>
                        <RadioGroup value={mode} onValueChange={setMode} className="py-2">
                            <div className="flex items-start gap-2">
                                <RadioGroupItem value="faithful" id="cv-faithful" data-testid="convert-mode-faithful" />
                                <Label htmlFor="cv-faithful" className="cursor-pointer font-normal">
                                    <span className="font-medium">Faithful</span>{" "}
                                    <span className="text-muted-foreground text-xs">
                                        &mdash; keep original wording, AI only adds structure
                                    </span>
                                </Label>
                            </div>
                            <div className="flex items-start gap-2">
                                <RadioGroupItem value="improved" id="cv-improved" data-testid="convert-mode-improved" />
                                <Label htmlFor="cv-improved" className="cursor-pointer font-normal">
                                    <span className="font-medium">Improved</span>{" "}
                                    <span className="text-muted-foreground text-xs">
                                        &mdash; AI rewrites for clarity, modernises tone
                                    </span>
                                </Label>
                            </div>
                            <div className="flex items-start gap-2">
                                <RadioGroupItem value="replica" id="cv-replica" data-testid="convert-mode-replica" />
                                <Label htmlFor="cv-replica" className="cursor-pointer font-normal">
                                    <span className="font-medium">Replica (page images)</span>{" "}
                                    <span className="text-muted-foreground text-xs">
                                        &mdash; pixel-perfect: every PDF page becomes an inline image
                                    </span>
                                </Label>
                            </div>
                            <div className="flex items-start gap-2">
                                <RadioGroupItem value="slideshow" id="cv-slideshow" data-testid="convert-mode-slideshow" />
                                <Label htmlFor="cv-slideshow" className="cursor-pointer font-normal">
                                    <span className="font-medium">Slideshow</span>{" "}
                                    <span className="text-accent text-xs font-semibold uppercase tracking-wide">Recommended</span>{" "}
                                    <span className="text-muted-foreground text-xs">
                                        &mdash; full-screen presentation viewer: students navigate the PDF page-by-page with prev/next, arrow keys, or swipe. Bypasses the file-download issues.
                                    </span>
                                </Label>
                            </div>
                        </RadioGroup>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                data-testid="convert-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={submit}
                                disabled={submitting}
                                className="gap-1.5"
                                data-testid="convert-start"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                                Convert
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <div className="space-y-3 py-2" data-testid="convert-progress">
                        <div className="flex items-center gap-2">
                            {job.status === "done" ? (
                                <CheckCircle2 className="h-5 w-5 text-primary" />
                            ) : job.status === "failed" ? (
                                <AlertCircle className="h-5 w-5 text-destructive" />
                            ) : (
                                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                            )}
                            <div className="text-sm font-medium">{statusLine}</div>
                        </div>
                        {job.error ? (
                            <div className="text-xs text-destructive" data-testid="convert-error">
                                {job.error}
                            </div>
                        ) : null}
                        {finished ? (
                            <DialogFooter>
                                <Button onClick={() => onOpenChange(false)} data-testid="convert-close">
                                    Close
                                </Button>
                            </DialogFooter>
                        ) : null}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
